import { UseQueryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { debug } from 'application/Shared/Utils';
import axios from 'axios';
import { evaluate } from 'cel-js';
import { useMemo } from 'react';

import { templateInterpolation } from '../Helpers/templateInterpolation';
import {
    type BasePanel,
    Context,
    Params,
    RESTDataSource,
    StaticDataSource
} from '../types';
import { transformers } from './transformers';

/** We refetch the data every 10 minutes */
const defaultRefetchInterval = 10 * 60 * 1000;

/** We consider the data stale after 10 minutes */
const defaultStaleTime = 10 * 60 * 1000;

/**
 * Interpolate the params replacing the variables with the values from the context.
 * @param params - The parameters of the API.
 * @param context - The context of the API. If not provided, the context will be empty.
 * @returns The interpolated parameters.
 */
function interpolateParams(params: Params, context: Context): Params {
    if (!params) return {};
    return Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
            key,
            templateInterpolation(value, context.variables)
        ])
    );
}

/**
 * Apply transformation to the raw data.
 * @param data - The raw data to transform
 * @param transformer - The transformer to apply
 * @returns The transformed data
 */
function applyTransformation(
    data: unknown,
    transformer: RESTDataSource['transformer']
) {
    if (!transformer) {
        return data;
    }

    // Handle CEL transformer object
    if (typeof transformer === 'object' && transformer.type === 'CEL') {
        try {
            const result = evaluate(transformer.expression, { data });
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Unknown error';
            throw new Error(
                `CEL transformer evaluation failed: ${errorMessage}`
            );
        }
    }

    // Handle string transformer (existing functionality)
    if (typeof transformer === 'string') {
        if (!(transformer in transformers)) {
            throw new Error(`Transformer ${transformer} not found`);
        }

        // eslint-disable-next-line import/namespace
        return transformers[transformer as keyof typeof transformers]({
            data
        });
    }

    throw new Error('Invalid transformer format');
}

/**
 * Get the raw data from the REST API (without transformation).
 */
async function getRestData(
    { url, method, params }: Pick<RESTDataSource, 'url' | 'method' | 'params'>,
    signal: AbortSignal
) {
    const response = await axios(url, { method, params, signal });

    const { data } = response.data;

    // sleep for a second to simulate a slow request
    // TODO: Remove this once we finish working on the dashboard
    // await new Promise((resolve) => {
    //     setTimeout(resolve, 2000);
    // });

    return data;
}

/**
 * Get the data from the static source.
 */
async function getStaticData(data: StaticDataSource) {
    return data.values;
}

/**
 * Use the React Query hook to fetch the data from the REST API or the static source.
 * @param panel - The panel to fetch the data from.
 * @param context - The context of the panel.
 *
 * @returns The data from the REST API or the static source.
 */
export function useReactQuery(
    panel: Partial<BasePanel>,
    context: Context,
    refreshDashboard: number | null
) {
    const queryOptions: Partial<UseQueryOptions> = {};

    if (!panel.data) {
        queryOptions.queryFn = skipToken;
    } else {
        switch (panel.data.source) {
            case 'rest': {
                const {
                    method,
                    params: rawParams = {},
                    requiredParams,
                    url: rawUrl
                } = panel.data as RESTDataSource;

                // Interpolate the params replacing the variables with the values from the context
                const intermediateParams = interpolateParams(
                    rawParams,
                    context
                );
                // Remove any params with null value
                const params = Object.fromEntries(
                    Object.entries(intermediateParams).filter(
                        ([, value]) => value
                    )
                );

                const url =
                    templateInterpolation(rawUrl, context.variables) || '';

                // Check required params
                const hasAllRequired = requiredParams?.every(
                    (key) => params![key] !== undefined && params![key] !== ''
                );

                // Remove transformer from queryKey to allow caching of raw data
                queryOptions.queryKey = [method, url, params, panel.id];
                queryOptions.queryFn = async ({ signal }) =>
                    getRestData({ method, params, url }, signal);

                // If the panel has no data, don't fetch it
                queryOptions.enabled = hasAllRequired;
                break;
            }
            case 'static':
                queryOptions.queryFn = async () =>
                    getStaticData(panel.data as StaticDataSource);
                break;
            default:
                throw new Error('Invalid data source');
        }
    }

    // If the refresh dashboard is a number, set the refetch interval and stale time
    if (typeof refreshDashboard === 'number') {
        queryOptions.refetchInterval = refreshDashboard * 1000;
        queryOptions.staleTime = refreshDashboard * 1000;
    }

    // If the refresh dashboard is null, we disable the refresh
    if (refreshDashboard === null) {
        queryOptions.refetchInterval = false;
        queryOptions.staleTime = Infinity;
    }

    const query = useQuery({
        // If no queryKey is provided, use the panel id
        queryKey: queryOptions.queryKey ?? [panel.id],
        refetchInterval: defaultRefetchInterval,
        staleTime: defaultStaleTime,
        ...queryOptions
    });

    // Apply transformation to the cached data
    const transformedData = useMemo(() => {
        if (!query.data || !panel.data || panel.data.source !== 'rest') {
            return query.data;
        }

        const { transformer } = panel.data as RESTDataSource;

        try {
            return applyTransformation(query.data, transformer);
        } catch (error) {
            debug.error('Transformation error:', error);
            // Return raw data if transformation fails
            return query.data;
        }
    }, [query.data, panel.data]);

    // Return the query object with transformed data
    return {
        ...query,
        data: transformedData
    };
}
